import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import {
  Typography,
  List,
  ListItem,
  IconButton,
  Paper,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { svSE } from '@mui/x-date-pickers/locales';
import { v4 as uuidv4 } from 'uuid';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import { IMessage } from '../../lib/interfaces';
import {
  clearError,
  clearMessageContent,
  createMessage,
  pollUpdatedMessages,
  updateMessageContent,
} from '../../store/reducers/messageBoard';
import { clearSelectedTemplate } from '../../store/reducers/templates';
import { RootState } from '../../store/types/sharedTypes';
import { AppDispatch } from '../../store';
import { formatTimestamp } from '../../utils/dateUtils';
import ToastMessageInput from '../../utils/ToastMessageInput';
import ToastMarkdownViewer from '../../utils/ToastMarkdownViewer';

import TemplateSelector from '../Template/TemplateSelector';

dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

const MessageBoardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 60vh;
  padding: 0;
  background-color: white;
`;

const MessageListContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 0;
  background-color: white;
`;

const EmptyMessageContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  color: gray;
  padding: 16px;
`;

const MessageItem = styled(ListItem)`
  display: block;
  padding-bottom: 8px;
  padding-left: 0;
`;

const MessageBubble = styled(Paper)<{ isSystemMessage?: boolean }>`
  display: inline-block;
  background-color: ${(props) =>
    props.isSystemMessage ? '#e0f7fa' : '#f0f0f0'};
  padding: 6px 10px;
  border-radius: 8px;
  margin-top: 4px;
  font-style: ${(props) => (props.isSystemMessage ? 'italic' : 'normal')};
`;

const InputContainer = styled.div`
  display: flex;
  padding: 0;
  margin: 0;
  margin-top: 8px;
  align-items: center;
  gap: 10px;
`;

const OptionsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  margin: 8px 0 4px 0;
`;

const ComposeOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  flex: 1;
`;

const POLL_UPDATED_MESSAGES_INTERVAL = 60000;

const getReadStatusKey = (message: IMessage) => {
  if (message.requiresConfirmation) {
    if (message.confirmationAt) {
      return 'inbox.confirmed';
    }
    if (message.readAt) {
      return 'inbox.awaitingConfirmation';
    }
    return 'inbox.unread';
  }
  if (message.readAt) {
    return 'inbox.read';
  }
  return 'inbox.unread';
};

const getReadStatusColor = (message: IMessage) => {
  if (message.requiresConfirmation) {
    if (message.confirmationAt) {
      return 'green';
    }
    return '#FF9900';
  }
  if (message.readAt) {
    return 'green';
  }
  return 'red';
};

const MessageBoard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const { messages, lastUpdateTimestamp, messageContent, error } = useSelector(
    (state: RootState) => state.messageBoard
  );

  const { sessionUser, patientDetails } = useSelector(
    (state: RootState) => state.general
  );

  const receiverId = patientDetails?._id;

  const [requiresConfirmation, setRequiresConfirmation] = useState(false);
  const [confirmationDueAt, setConfirmationDueAt] = useState<string>('');
  const [confirmationError, setConfirmationError] = useState<boolean>(false);

  const safeToIso = (val?: string | null) => {
    if (!val) return undefined;
    const d = new Date(val);
    return Number.isFinite(d.getTime()) ? d.toISOString() : undefined;
  };

  const handleCloseError = () => {
    dispatch(clearError());
  };

  useEffect(
    () => () => {
      dispatch(clearSelectedTemplate());
    },
    [dispatch]
  );

  useEffect(() => {
    if (receiverId) {
      const interval = setInterval(() => {
        if (lastUpdateTimestamp) {
          dispatch(
            pollUpdatedMessages({ receiverId, afterDate: lastUpdateTimestamp })
          );
        }
      }, POLL_UPDATED_MESSAGES_INTERVAL);

      return () => clearInterval(interval);
    }
    return () => {};
  }, [receiverId, lastUpdateTimestamp, dispatch]);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current!.scrollIntoView();
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const templateContext: Record<string, string | null> = {
    patient_first_name: patientDetails?.firstName ?? null,
    clinic_phone: null,
    appointment_date: null,
    appointment_time: null,
  };

  const handleSendMessage = async () => {
    if (!sessionUser || !patientDetails?._id) return;

    const trimmedContent = (messageContent || '').trim();
    if (trimmedContent === '') return;

    if (requiresConfirmation && confirmationError) {
      return;
    }

    const base: IMessage = {
      _id: uuidv4(),
      senderId: sessionUser._id,
      senderName: `${sessionUser.firstName} ${sessionUser.lastName}`,
      senderProfession: sessionUser.profession,
      receiverId: patientDetails._id,
      content: trimmedContent,
      readAt: null,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const extras: Partial<IMessage> = {};

    if (requiresConfirmation) {
      extras.requiresConfirmation = true;

      if (confirmationDueAt) {
        const dueIso = safeToIso(confirmationDueAt);
        if (!dueIso) {
          setConfirmationError(true);
          return;
        }
        extras.confirmationDueAt = dueIso;
      }
    }

    const newMessage: IMessage = { ...base, ...extras };

    const action = await dispatch(createMessage(newMessage));

    if (createMessage.fulfilled.match(action)) {
      dispatch(clearMessageContent());
      setRequiresConfirmation(false);
      setConfirmationDueAt('');
      setConfirmationError(false);
    }
  };

  const isAdmin = !!sessionUser?.isAdmin;

  return (
    <MessageBoardWrapper>
      <MessageListContainer>
        {messages.length === 0 ? (
          <EmptyMessageContainer>
            <Typography variant="body1">{t('noMessages')}</Typography>
          </EmptyMessageContainer>
        ) : (
          <List disablePadding>
            {messages.map((message) => {
              const { isSystemMessage } = message;

              const senderTag = isSystemMessage
                ? t('systemMessageTag')
                : `${message.senderName}${
                    message.senderProfession
                      ? ` (${t(message.senderProfession)})`
                      : ''
                  }`;

              const messageDate = dayjs(message.createdAt);
              const timeDisplay = formatTimestamp(message.createdAt);
              const fullDateTime = messageDate.format('YYYY-MM-DD HH:mm:ss');

              const statusKey = getReadStatusKey(message);
              const readStatus = t(statusKey);

              return (
                <MessageItem key={message._id}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      fontStyle: isSystemMessage ? 'italic' : 'normal',
                    }}
                    title={fullDateTime}
                  >
                    {senderTag}{' '}
                    <Typography
                      variant="caption"
                      component="span"
                      sx={{ color: 'textSecondary' }}
                      title={fullDateTime}
                    >
                      {timeDisplay}
                    </Typography>
                  </Typography>
                  <MessageBubble
                    isSystemMessage={isSystemMessage}
                    elevation={0}
                  >
                    <div style={{ fontSize: '0.9rem' }}>
                      <ToastMarkdownViewer markdown={message.content || ''} />
                    </div>
                  </MessageBubble>

                  <Typography
                    variant="caption"
                    component="span"
                    sx={{
                      marginTop: '4px',
                      color: getReadStatusColor(message),
                      display: 'block',
                    }}
                  >
                    {readStatus}
                  </Typography>

                  {message.requiresConfirmation &&
                    message.confirmationDueAt &&
                    !message.confirmationAt && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: 'text.secondary' }}
                      >
                        {t('inbox.dueBy')}:{' '}
                        {dayjs(message.confirmationDueAt).format(
                          'YYYY-MM-DD HH:mm'
                        )}
                      </Typography>
                    )}
                  {message.requiresConfirmation && message.confirmationAt && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: 'text.secondary' }}
                    >
                      {t('inbox.confirmationAt')}:{' '}
                      {dayjs(message.confirmationAt).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  )}
                </MessageItem>
              );
            })}
          </List>
        )}
        <div ref={messagesEndRef} />
      </MessageListContainer>

      <OptionsRow>
        {isAdmin && (
          <Box sx={{ maxWidth: 456, width: '100%' }}>
            <TemplateSelector
              size="small"
              context={templateContext}
              onInsert={(rendered) => dispatch(updateMessageContent(rendered))}
            />
          </Box>
        )}

        <ComposeOptions>
          <FormControlLabel
            control={
              <Checkbox
                checked={requiresConfirmation}
                onChange={(e) => {
                  const { checked } = e.target;
                  setRequiresConfirmation(checked);
                  if (!checked) {
                    setConfirmationDueAt('');
                    setConfirmationError(false);
                  }
                }}
                color="primary"
              />
            }
            label={t('inbox.requiresConfirmation')}
          />

          {requiresConfirmation && (
            <Tooltip title={t('inbox.dueByHelp')}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="sv"
                localeText={
                  svSE.components.MuiLocalizationProvider.defaultProps
                    .localeText
                }
              >
                <DateTimePicker
                  label={t('inbox.dueBy')}
                  value={confirmationDueAt ? dayjs(confirmationDueAt) : null}
                  disablePast
                  minDateTime={dayjs()}
                  onChange={(value) => {
                    if (!value) {
                      setConfirmationDueAt('');
                      setConfirmationError(false);
                      return;
                    }

                    if (!value.isValid() || value.isBefore(dayjs())) {
                      setConfirmationDueAt('');
                      setConfirmationError(true);
                      return;
                    }

                    setConfirmationError(false);
                    setConfirmationDueAt(value.toISOString());
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 240 },
                      error: confirmationError,
                    },
                  }}
                />
              </LocalizationProvider>
            </Tooltip>
          )}
        </ComposeOptions>
      </OptionsRow>

      <InputContainer>
        <div style={{ flex: 1 }}>
          <ToastMessageInput
            value={messageContent || ''}
            onChange={(md) => dispatch(updateMessageContent(md))}
            onSend={handleSendMessage}
            placeholder={t('typeAMessage') as string}
          />
        </div>
        <InputAdornment
          position="end"
          sx={{
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
            pb: 0,
            ml: 0.5,
          }}
        >
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={
              (messageContent || '').trim() === '' ||
              (requiresConfirmation && confirmationError)
            }
            size="medium"
            aria-label={t('send') as string}
            sx={{
              borderRadius: '10px',
              px: 1,
              py: 0.5,
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      </InputContainer>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('inbox.genericError')}
        </Alert>
      </Snackbar>
    </MessageBoardWrapper>
  );
};

export default MessageBoard;
